/**
 * "The Fog Lifts" — the replay screen. The ordinary map renderer draws the replay frames that
 * replay/store.ts streams into the game store; around it sits the projection room: a title bar
 * with the date and the perspective selector (the whole truth, or what one staff knew), the
 * newsreel deck along the bottom (transport, speed, a timeline of the war's big moments over a
 * victory-point strip), and the familiar teletype, minimap, hex readout and read-only unit card.
 */
import { dayOf, type FactionId, hourOfDay, isNight, SPECTATOR } from '@friction/engine';
import {
  type CSSProperties,
  type PointerEvent,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { bus } from '../../game/bus';
import { cssColor, formatDate, formatHour } from '../../game/format';
import { gameStore, useGame } from '../../game/store';
import { useT } from '../../i18n';
import { nationLabel, theatreTitle } from '../../i18n/names';
import { localizeServerText } from '../../net/serverText';
import {
  closeReplay,
  REPLAY_SPEEDS,
  replayStore,
  seekTo,
  setPerspective,
  setPlaying,
  setSpeed,
  setTruth,
  stepBy,
  useReplay,
} from '../../replay/store';
import {
  adjacentMarker,
  type MarkerKind,
  markerNear,
  type ReplayMarker,
  type ReplayTimeline,
} from '../../replay/timeline';
import { Button, cx, Keycap } from '../common/controls';
import { Icon } from '../common/Icon';
import { useMapRenderer } from '../game/GameScreen';
import { HexReadout } from '../game/HexReadout';
import hud from '../game/hud.module.css';
import { Minimap } from '../game/Minimap';
import { SelectionPanel } from '../game/SelectionPanel';
import { Teletype } from '../game/Teletype';
import s from './replay.module.css';
import { SaveRecordButton } from './SaveRecord';

/** Height of the newsreel deck (HUD px): the teletype, minimap and readout sit above it. */
const DECK_H = 142;

const kindKey = (kind: MarkerKind) => `rp.kind.${kind}` as const;

// ---- keyboard -----------------------------------------------------------------------------------

function nextPerspective(dir: 1 | -1): FactionId {
  const r = replayStore.getState();
  const ids = [SPECTATOR, ...(r.timeline?.nations.map((n) => n.id) ?? [])];
  const i = Math.max(0, ids.indexOf(r.perspective));
  return ids[(i + dir + ids.length) % ids.length];
}

function useReplayHotkeys(): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.target instanceof Element && e.target.closest('input, textarea, select')) return;
      const r = replayStore.getState();
      const g = gameStore.getState();
      const day = e.shiftKey ? 24 : 6;
      switch (e.key) {
        case 'Escape':
          if (g.selection.length || g.inspectedContact !== null) {
            g.select([]);
            g.inspectContact(null);
          } else closeReplay();
          return;
        case ' ':
          e.preventDefault();
          setPlaying(!r.playing);
          return;
        case 'ArrowRight':
          e.preventDefault();
          stepBy(day);
          return;
        case 'ArrowLeft':
          e.preventDefault();
          stepBy(-day);
          return;
        case 'Home':
          seekTo(0);
          return;
        case 'End':
          seekTo(r.timeline?.endTick ?? 0);
          return;
        case ']':
        case '[': {
          const m = r.timeline && adjacentMarker(r.timeline.markers, r.tick, e.key === ']' ? 1 : -1);
          if (m) showMarker(m);
          return;
        }
        case '+':
        case '=':
        case '-': {
          const i = REPLAY_SPEEDS.indexOf(r.speed) + (e.key === '-' ? -1 : 1);
          setSpeed(REPLAY_SPEEDS[Math.max(0, Math.min(REPLAY_SPEEDS.length - 1, i))]);
          return;
        }
        case 'v':
        case 'V':
          setPerspective(nextPerspective(e.shiftKey ? -1 : 1));
          return;
        case 't':
        case 'T':
          setTruth(!r.truth);
          return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

/** Jump to a big moment and look at it (close enough to read the counters). */
function showMarker(m: ReplayMarker): void {
  seekTo(m.tick);
  if (m.hex >= 0) bus.emit('focusHex', { hex: m.hex, zoom: 1.15 });
}

// ---- title bar ----------------------------------------------------------------------------------

/** Day, hour and a 24 h dial (the replay's tick, or the live war's for an observer). */
export function ReelClock({ tick }: { tick: number }) {
  const t = useT();
  const night = isNight(tick);
  return (
    <div className={s.clock} title={formatDate(tick, t.locale)}>
      <Icon name={night ? 'moon' : 'sun'} size={17} className={night ? s.moon : s.sun} />
      <span className={s.day}>{t('time.day', { n: dayOf(tick) })}</span>
      <span className={s.hour}>{formatHour(tick)}</span>
      <span className={s.dial} aria-hidden>
        <span className={s.needle} style={{ left: `${((hourOfDay(tick) + 0.5) / 24) * 100}%` }} />
      </span>
    </div>
  );
}

export interface PerspectiveNation {
  id: FactionId;
  name: string;
  adjective: string;
  color: number;
}

/**
 * Whose map: the whole truth, or one chip per nation (fallen nations struck through), and — in
 * the replay — the Truth toggle.
 */
export function PerspectiveBar({
  nations,
  perspective,
  onPick,
  truth,
}: {
  nations: readonly PerspectiveNation[];
  perspective: FactionId;
  onPick(p: FactionId): void;
  truth?: { on: boolean; toggle(): void };
}) {
  const t = useT();
  const alive = useGame((g) => g.view?.factions.map((f) => f.alive).join());
  const living = alive?.split(',');
  return (
    <div className={s.persp}>
      <button
        type="button"
        aria-pressed={perspective === SPECTATOR}
        className={cx(s.pBtn, perspective === SPECTATOR && s.pOn)}
        onClick={() => onPick(SPECTATOR)}
        title={t('rp.omniscient.hint')}
      >
        <Icon name="eye" size={15} />
        {t('rp.omniscient')}
      </button>
      {nations.map((n, i) => (
        <button
          key={n.id}
          type="button"
          aria-pressed={perspective === n.id}
          className={cx(s.pBtn, perspective === n.id && s.pOn, living?.[i] === 'false' && s.pFallen)}
          style={{ '--fc': cssColor(n.color) } as CSSProperties}
          onClick={() => onPick(n.id)}
          title={t('rp.nation.hint', { adj: n.adjective })}
        >
          <i className={s.swatch} />
          {nationLabel(n, t.locale)}
        </button>
      ))}
      {truth && (
        <button
          type="button"
          className={cx(s.truthBtn, truth.on && s.truthOn)}
          disabled={perspective === SPECTATOR}
          aria-pressed={truth.on}
          onClick={truth.toggle}
          title={t('rp.truth.hint')}
        >
          <Icon name="focus" size={15} />
          {t('rp.truth')}
        </button>
      )}
    </div>
  );
}

/** Newsreel intertitle under the title bar: whose picture of the war this is. */
export function Intertitle({ nation, truth }: { nation: PerspectiveNation | null; truth: boolean }) {
  const t = useT();
  if (!nation)
    return (
      <div className={s.caption}>
        <span className={s.capMain}>{t('rp.whole')}</span>
        <span className={s.capSub}>{t('rp.whole.sub')}</span>
      </div>
    );
  return (
    <div className={s.caption} style={{ '--fc': cssColor(nation.color) } as CSSProperties}>
      <span className={s.capMain}>{t('rp.seenBy', { adj: nation.adjective })}</span>
      <span className={s.capSub}>
        {t('rp.seenBy.sub')}
        {truth ? t('rp.seenBy.truth') : ''}
      </span>
    </div>
  );
}

function TitleBar() {
  const t = useT();
  const timeline = useReplay((r) => r.timeline);
  const tick = useReplay((r) => r.tick);
  const perspective = useReplay((r) => r.perspective);
  const truth = useReplay((r) => r.truth);
  const archive = useReplay((r) => r.source === 'archive');
  const name = useGame((g) => g.map?.name ?? '');
  return (
    <header className={s.top}>
      <div className={s.brand}>
        <span className={s.brandTitle}>{t('rp.title')}</span>
        <span className={s.brandSub}>{name && theatreTitle(name, t.locale)}</span>
      </div>
      <ReelClock tick={tick} />
      {timeline && (
        <PerspectiveBar
          nations={timeline.nations}
          perspective={perspective}
          onPick={setPerspective}
          truth={{ on: truth, toggle: () => setTruth(!truth) }}
        />
      )}
      <div className={s.topEnd}>
        {/* An archive film is already on disk; the war just fought can be saved from here. */}
        {!archive && <SaveRecordButton variant="ghost" />}
        <Button variant="ghost" icon="exit" hotkey="Esc" onClick={closeReplay}>
          {archive ? t('rp.backArchive') : t('rp.back')}
        </Button>
      </div>
    </header>
  );
}

/** Newsreel intertitle under the title bar: whose picture of the war this is. */
function Caption() {
  const perspective = useReplay((r) => r.perspective);
  const truth = useReplay((r) => r.truth);
  const nation = useReplay((r) => r.timeline?.nations.find((n) => n.id === r.perspective));
  return <Intertitle nation={perspective === SPECTATOR ? null : (nation ?? null)} truth={truth} />;
}

// ---- the deck -----------------------------------------------------------------------------------

const CHART_H = 34;
const MARK_Y = CHART_H + 12;
const TRACK_Y = CHART_H + 24;
const SVG_H = TRACK_Y + 22;

function useWidth(): [RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(el.clientWidth));
    ro.observe(el);
    setW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

function MarkerGlyph({ m, x, color }: { m: ReplayMarker; x: number; color: string }) {
  const y = MARK_Y;
  switch (m.kind) {
    case 'capture':
      return <path d={`M${x} ${y - 5}l4.5 5-4.5 5-4.5-5z`} fill={color} className={s.mkInk} />;
    case 'kessel':
      return (
        <circle cx={x} cy={y} r={4.3} fill="none" stroke={color} strokeWidth={2.2} className={s.mkRing} />
      );
    case 'intercept':
      return <rect x={x - 3} y={y - 3} width={6} height={6} fill="none" stroke={color} strokeWidth={1.5} />;
    case 'capitulation':
      return <path d={`M${x - 4} ${y - 4}l8 8m0-8l-8 8`} stroke="#e0634e" strokeWidth={2.2} />;
    case 'end':
      return (
        <path
          d={`M${x} ${y - 6}l1.8 3.9 4.2.4-3.2 2.8 1 4.2-3.8-2.2-3.8 2.2 1-4.2-3.2-2.8 4.2-.4z`}
          fill="#dcc08a"
        />
      );
    case 'war':
      return <path d={`M${x} ${y + 6}v-12h7l-2 3 2 3h-7`} fill="#dcc08a" stroke="#dcc08a" strokeWidth={1} />;
  }
}

function Film({ timeline }: { timeline: ReplayTimeline }) {
  const t = useT();
  const [box, width] = useWidth();
  const tick = useReplay((r) => r.tick);
  const share = useGame((g) => g.config?.vpVictoryShare ?? 0.6);
  const [hover, setHover] = useState<number | null>(null);
  const drag = useRef<{ resume: boolean } | null>(null);
  const end = Math.max(1, timeline.endTick);
  const W = Math.max(200, width);
  const x = (t: number) => (t / end) * W;
  const tickAt = (clientX: number, el: Element) => {
    const r = el.getBoundingClientRect();
    return Math.round(Math.max(0, Math.min(1, (clientX - r.left) / r.width)) * end);
  };
  const colors = useMemo(() => new Map(timeline.nations.map((n) => [n.id, cssColor(n.color)])), [timeline]);

  // Stacked victory-point bands (the balance of the war), scaled to all VP on the map.
  const bands = useMemo(() => {
    if (width <= 0) return [];
    const total = Math.max(1, timeline.totalVp);
    const step = Math.max(1, Math.ceil(end / Math.max(60, width / 3)));
    const ts: number[] = [];
    for (let t = 0; t < end; t += step) ts.push(t);
    ts.push(end);
    const base = ts.map(() => 0);
    const px = (t: number) => ((t / end) * W).toFixed(1);
    return timeline.nations.map((n) => {
      const series = timeline.vp[n.id] ?? [];
      const lower = base.slice();
      const upper = ts.map((t, i) => {
        base[i] += series[t] ?? 0;
        return base[i];
      });
      const y = (v: number) => CHART_H - (v / total) * CHART_H;
      const top = ts.map((t, i) => `${px(t)},${y(upper[i]).toFixed(1)}`);
      const bottom = ts.map((t, i) => `${px(t)},${y(lower[i]).toFixed(1)}`).reverse();
      return { id: n.id, points: [...top, ...bottom].join(' ') };
    });
  }, [timeline, width, end, W]);

  const days: number[] = [];
  const perDay = Math.max(1, Math.ceil(end / 24 / Math.max(1, W / 46)));
  for (let t = 18; t <= end; t += 24 * perDay) days.push(t); // tick 18 = Day 2, 00:00

  const onDown = (e: PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    const r = replayStore.getState();
    drag.current = { resume: r.playing };
    if (r.playing) setPlaying(false);
    const t = tickAt(e.clientX, e.currentTarget);
    // A click on a marker goes to that moment and shows it on the map.
    const m = markerNear(timeline.markers, t, (end / W) * 5);
    if (m) showMarker(m);
    else seekTo(t);
  };
  const onMove = (e: PointerEvent<HTMLDivElement>) => {
    const t = tickAt(e.clientX, e.currentTarget);
    setHover(t);
    if (drag.current) seekTo(t);
  };
  const onUp = () => {
    const d = drag.current;
    drag.current = null;
    if (d?.resume) setPlaying(true);
  };
  const near = hover !== null ? markerNear(timeline.markers, hover, Math.max(2, (end / W) * 5)) : null;
  const hx = hover !== null ? x(hover) : 0;

  return (
    <div className={s.film} ref={box}>
      {width > 0 && (
        <div
          className={s.scrub}
          role="slider"
          tabIndex={-1}
          aria-label={t('rp.timeline')}
          aria-valuemin={0}
          aria-valuemax={end}
          aria-valuenow={tick}
          aria-valuetext={formatDate(tick, t.locale)}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          onPointerLeave={() => setHover(null)}
        >
          <svg width={W} height={SVG_H} className={s.svg} aria-hidden>
            <rect x={0} y={0} width={W} height={CHART_H} className={s.chartBg} />
            {bands.map((b) => (
              <polygon key={b.id} points={b.points} fill={colors.get(b.id)} className={s.band} />
            ))}
            <line x1={0} x2={W} y1={CHART_H * (1 - share)} y2={CHART_H * (1 - share)} className={s.share} />
            <rect x={0} y={TRACK_Y - 3} width={W} height={6} rx={3} className={s.track} />
            <rect x={0} y={TRACK_Y - 3} width={x(tick)} height={6} rx={3} className={s.trackDone} />
            {days.map((day) => (
              <g key={day}>
                <line x1={x(day)} x2={x(day)} y1={TRACK_Y - 5} y2={TRACK_Y + 5} className={s.dayTick} />
                <text x={x(day) + 3} y={TRACK_Y + 16} className={s.dayLabel}>
                  {t('time.dayShort', { n: dayOf(day) })}
                </text>
              </g>
            ))}
            {timeline.markers.map((m) => (
              <MarkerGlyph key={m.id} m={m} x={x(m.tick)} color={colors.get(m.faction) ?? '#dcc08a'} />
            ))}
            {hover !== null && <line x1={hx} x2={hx} y1={0} y2={TRACK_Y + 6} className={s.hoverLine} />}
            <g transform={`translate(${x(tick)},0)`} className={s.head}>
              <line x1={0} x2={0} y1={0} y2={TRACK_Y + 6} />
              <circle cx={0} cy={TRACK_Y} r={6.5} />
            </g>
          </svg>
          {hover !== null && (
            <div className={s.tip} style={{ left: Math.max(90, Math.min(W - 90, hx)) }}>
              <b>{formatDate(near ? near.tick : hover, t.locale)}</b>
              {near && (
                <span>
                  <em>{t(kindKey(near.kind))}</em> {near.text[t.locale]}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Transport() {
  const t = useT();
  const playing = useReplay((r) => r.playing);
  const speed = useReplay((r) => r.speed);
  const tick = useReplay((r) => r.tick);
  const timeline = useReplay((r) => r.timeline);
  const verified = useReplay((r) => r.verified);
  const jump = (dir: 1 | -1) => {
    const m = timeline && adjacentMarker(timeline.markers, tick, dir);
    if (m) showMarker(m);
  };
  return (
    <div className={s.transport}>
      <div className={s.buttons}>
        <button type="button" className={s.tBtn} onClick={() => stepBy(-24)} title={t('rp.backDay')}>
          <span className={s.dbl}>
            <Icon name="chevronLeft" size={14} />
            <Icon name="chevronLeft" size={14} />
          </span>
        </button>
        <button type="button" className={s.tBtn} onClick={() => stepBy(-6)} title={t('rp.back6')}>
          <Icon name="chevronLeft" size={16} />
        </button>
        <button
          type="button"
          className={cx(s.tBtn, s.play)}
          onClick={() => setPlaying(!playing)}
          title={playing ? t('rp.pause') : t('rp.play')}
        >
          <Icon name={playing ? 'pause' : 'play'} size={18} />
        </button>
        <button type="button" className={s.tBtn} onClick={() => stepBy(6)} title={t('rp.fwd6')}>
          <Icon name="chevronRight" size={16} />
        </button>
        <button type="button" className={s.tBtn} onClick={() => stepBy(24)} title={t('rp.fwdDay')}>
          <span className={s.dbl}>
            <Icon name="chevronRight" size={14} />
            <Icon name="chevronRight" size={14} />
          </span>
        </button>
      </div>
      <div className={s.speeds}>
        {REPLAY_SPEEDS.map((v) => (
          <button
            key={v}
            type="button"
            aria-pressed={speed === v}
            className={cx(s.sBtn, speed === v && s.sOn)}
            onClick={() => setSpeed(v)}
            title={t('rp.speed', { n: v })}
          >
            {v}×
          </button>
        ))}
      </div>
      <div className={s.readout}>
        <span className={s.stamp}>{formatDate(tick, t.locale).toUpperCase()}</span>
        <span className={s.of}>{t('rp.ofDays', { n: timeline ? dayOf(timeline.endTick) : '—' })}</span>
      </div>
      <div className={s.moments}>
        <button type="button" className={s.mBtn} onClick={() => jump(-1)} title={t('rp.prev')}>
          <Icon name="chevronLeft" size={14} /> {t('rp.moment')}
        </button>
        <button type="button" className={s.mBtn} onClick={() => jump(1)} title={t('rp.next')}>
          {t('rp.moment')} <Icon name="chevronRight" size={14} />
        </button>
      </div>
      <div className={s.keys}>
        <Keycap>{t('key.space')}</Keycap> {t('rp.key.play')} <Keycap>←</Keycap>
        <Keycap>→</Keycap> {t('rp.key.hours')} <Keycap>{t('key.shift')}</Keycap> {t('rp.key.day')}{' '}
        <Keycap>V</Keycap> {t('rp.key.view')}
      </div>
      {verified !== null && (
        <span
          className={cx(s.seal, verified ? s.sealOk : s.sealBad)}
          title={verified ? t('rp.verified.hint') : t('rp.diverges.hint')}
        >
          {verified ? t('rp.verified') : t('rp.diverges')}
        </span>
      )}
    </div>
  );
}

function Deck() {
  const t = useT();
  const timeline = useReplay((r) => r.timeline);
  return (
    <section className={cx(hud.plate, s.deck)} aria-label={t('rp.deck')} style={{ height: DECK_H - 12 }}>
      <Transport />
      {timeline && <Film timeline={timeline} />}
    </section>
  );
}

function Projector() {
  const t = useT();
  const status = useReplay((r) => r.status);
  const progress = useReplay((r) => r.progress);
  const error = useReplay((r) => r.error);
  if (status === 'ready') return null;
  return (
    <div className={s.projector}>
      <div className={s.reel}>
        {status === 'error' ? (
          <>
            <div className={s.reelTitle}>{t('rp.torn')}</div>
            <div className={s.reelSub}>{error ? localizeServerText(error, t) : t('err.replay')}</div>
            <Button variant="primary" icon="exit" onClick={closeReplay}>
              {t('rp.back')}
            </Button>
          </>
        ) : (
          <>
            <div className={s.reelTitle}>{t('rp.threading')}</div>
            <div className={s.reelSub}>{t('rp.threading.sub')}</div>
            <div className={s.bar}>
              <i style={{ width: `${Math.round(progress * 100)}%` }} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function ReplayScreen() {
  const mapRef = useRef<HTMLDivElement>(null);
  useMapRenderer(mapRef);
  useReplayHotkeys();
  const ready = useReplay((r) => r.status === 'ready');
  return (
    <div className={hud.screen}>
      <div ref={mapRef} className={hud.map} />
      <div className={cx(hud.vignette, s.flicker)} />
      <div className={cx(hud.hud, s.hud)} style={{ '--deck-h': `${DECK_H}px` } as CSSProperties}>
        <TitleBar />
        {ready && <Caption />}
        {ready && <SelectionPanel />}
        {ready && <Teletype />}
        <HexReadout />
        {ready && <Minimap />}
        <Deck />
        <Projector />
      </div>
    </div>
  );
}
